/**
 * @file   obj.js 针对对象封装的一些常用方法
 * @author zhengchanglong<zhengchanglong@baidu.com>
 * @date   2019-09-04
 */

import {locker} from './cache';

/**
 * 避免对象自定义了hasOwnProperty方法导致错误
 *
 * var foo = {
 *     hasOwnProperty: function() {
 *         return false;
 *     },
 *     bar: 'Here be dragons'
 * };
 *
 * foo.hasOwnProperty('bar'); // 始终返回 false
 *
 * @param  {Object} object
 * @param  {string} prop
 * @return {boolean}
 */
export function hasOwnProperty(object, prop) {
    return ({}).hasOwnProperty.call(object, prop);
}

/**
 * 封装Object.keys方法
 *
 * @param  {Object} object
 * @return {Array}
 */
export function keys(object) {
    return isObject(object) ? Object.keys(object) : [];
}

/**
 * 遍历对象可枚举属性
 *
 * @param  {Object} object
 * @param  {Function} fn
 */
export function each(object, fn) {
    keys(object).forEach(key => fn(object[key], key));
}

/**
 * Array-like reduce for objects.
 *
 * @param  {Object} object
 * @param  {Function} fn
 * @param  {Mixed} [initial = 0]
 * @return {Mixed}
 */
export function reduce(object, fn, initial = 0) {
    return keys(object).reduce((accum, key) => fn(accum, object[key], key), initial);
}

/**
 * Object.assign-style object shallow merge/extend.
 *
 * @param  {Object} target
 * @param  {Object} ...objects
 * @return {Object}
 */

export function assign(target, ...objects) {
    if (Object.assign) {
        return Object.assign(target, ...objects);
    }

    objects.reduce((accum, object) => {
        if (!isPlain(object)) {
            return;
        }

        each(object, (value, key) => {
            accum[key] = value;
        });
    }, target);

    return target;
}

/**
 * Merge two objects.
 *
 * @param  {Object} target
 * @param  {Object} ...sources
 * @return {Object} new object
 */
export function merge(target, ...sources) {
    if (!sources.length) {
        return target;
    }
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) {
                    Object.assign(target, {[key]: {}});
                }
                merge(target[key], source[key]);
            }
            else {
                Object.assign(target, {[key]: source[key]});
            }
        }
    }

    return merge(target, ...sources);
}

/**
 * Returns whether a value is an object of any kind - including DOM nodes,
 * arrays, regular expressions, etc. Not functions, though.
 *
 * This avoids the gotcha where using `typeof` on a `null` value
 * results in `'object'`.
 *
 * @param  {Object} value
 * @return {boolean}
 */
export function isObject(value) {
    return !!value && typeof value === 'object';
}

/**
 * Returns whether an object appears to be a "plain" object - that is, a
 * direct instance of `Object`.
 *
 * @param  {Object} value
 * @return {boolean}
 */
export function isPlain(value) {
    return isObject(value)
        && toString.call(value) === '[object Object]'
        && value.constructor === Object;
}

/**
 * Return an object by appointed props
 *
 * @param  {Object} object
 * @param  {Array} props
 * @return {Object}
 */
export function pickup(object, props) {
    const result = {};
    each(object, function (value, key) {
        if (hasOwnProperty(object, key) && props.includes(key)) {
            result[key] = value;
        }
    });

    return result;
}

/**
 * Return an object by unassigned props
 *
 * @param  {Object} object
 * @param  {Array} props
 * @return {Object}
 */
export function kickout(object, props) {
    const result = {};
    each(object, function (value, key) {
        if (hasOwnProperty(object, key) && !props.includes(key)) {
            result[key] = value;
        }
    });

    return result;
}

/**
 * Returns an object with an invalid key removed.
 *
 * @param  {Object} object
 * @param  {Array} items
 * @return {Object}
 */
export function trim(object, items = [undefined, '', null, NaN]) {
    each(object, (item, key) => {
        if (hasOwnProperty(object, key) && items.includes(item)) {
            delete object[key];
        }
    });

    return object;
}

/**
 * 遍历式深拷贝
 *
 * @param  {Object} object
 * @return {Object}
 */
export function clone(object) {
    if (Array.isArray(object)) {
        const result = [];
        object.forEach(item => {
            if (isObject(item)) {
                result.push(clone(item));
            }
            else {
                result.push(item);
            }
        });
        return result;
    }

    if (!isPlain(object)) {
        throw new TypeError('不支持克隆该类型的参数');
    }

    const root = {};
    const stack = [
        {
            parent: root,
            key: undefined,
            data: object
        }
    ];

    while (stack.length) {
        const node = stack.pop();
        const parent = node.parent;
        const key = node.key;
        const data = node.data;
        let attachedNode = parent;

        if (key !== undefined) {
            attachedNode = parent[key] = {};
        }

        locker.save({
            source: data,
            target: attachedNode
        });
        each(data, (value, key) => {
            if (hasOwnProperty(data, key)) {
                if (isPlain(value)) {
                    let cachedValue = null;
                    locker.each(item => {
                        if (item.source === value) {
                            cachedValue = item;
                        }
                    });
                    stack.push({
                        parent: attachedNode,
                        key: key,
                        data: cachedValue ? cachedValue.target : value
                    });
                }
                else if (Array.isArray(value)) {
                    // @TODO 这里只简单处理Array的情况
                    attachedNode[key] = [];
                    value.forEach(item => {
                        let v = isObject(item) ? JSON.parse(JSON.stringify(item)) : item;
                        attachedNode[key].push(v);
                    });
                }
                else {
                    attachedNode[key] = value;
                }
            }
        });
    }

    return root;
}
